import { ChangeEvent, CSSProperties, useEffect, useState } from 'react';
import clsx from 'clsx';
import {
	CryptoTokens,
	cryptoTokens,
	getBankAccountString,
	IntegrationPlatform,
	IntegrationProvider,
	IntegrationStatus,
	PaymentOptions,
	PaymentPlatform,
} from '@received/pricing-model';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Icon, Input, MenuList, Select, Switch, TextArea, Tooltip, CheckedTag, CheckedTagsList, IconImgType, StripeCardSelect } from '../_uiComponents';
import { PricingModelTab } from '../../types/contractTypes';
import { Store } from '../../types/storeTypes';
import { UserRole } from '../../types/userTypes';
import { getSuppliers } from '../../utils/CustomerUtils';
import { CreateSupplier, StripeConnectModal } from '..';
import { UpdatePricingTerms } from '../TermsAndSettings/TermsAndSettings';
import { images } from '../../constants/images';
import { PaymentMethodOptions } from '../../types/generalTypes';
import { onCancelIntegration, onConnectEntity, onMissingDetailsEntity } from '../../services/integrationService/integrationService';
import { getImageSrcByName, paymentOptions as constPaymentOptions } from './PricingTerms.utils';
import { BillingAccountDetails, Customer } from '../../types/customerTypes';
import { IntegrationType, StripeCard } from '../../types/integrationTypes';
import { bankTransferSettings, PricingTermsSettings } from './settings';
import styles from './PricingTerms.module.scss';

export class CryptoWallet {
	cryptoWalletAddress?: string;
	isCryptoWalletEnabled: boolean;
	tokens: CheckedTag[];
}

interface PricingTermsProps {
	className?: string;
	style?: CSSProperties;
	pricingModelTab?: PricingModelTab;
	disabled?: boolean;
	customerId?: string;
	// Hide internal "Settings" title; consumer renders its own header.
	hideTitle?: boolean;
	// Whether initSettings may persist the wire-only pre-selection. Only true for draft contracts — a
	// non-draft (e.g. Canceled contract re-entered into edit mode) must not have its billing rewritten just
	// by landing on the Billing settings step. Defaults to true to preserve behavior for other callers.
	allowAutoPersist?: boolean;
	updatePricingTermsData: ({ note, supplierId, billingDetails, cryptoSettings }: UpdatePricingTerms, instantUpdate?: boolean) => void;
}

export const PricingTermsV2 = ({
	disabled,
	pricingModelTab,
	className,
	style,
	hideTitle,
	allowAutoPersist = true,
	updatePricingTermsData,
}: PricingTermsProps) => {
	const [banks, setBanks] = useState<BillingAccountDetails[]>([]);
	const [isCreateEntityOpened, setIsCreateEntityOpened] = useState(false);
	// Entity being edited via per-item pencil; undefined = create mode.
	const [editEntity, setEditEntity] = useState<Customer | undefined>();
	// Which provider's connect-modal is open (null = none).
	const [newPaymentAccountPlatform, setNewPaymentAccountPlatform] = useState<PaymentPlatform | null>(null);
	const [isProviderChooserOpen, setIsProviderChooserOpen] = useState(false);
	const [supplierEntities, setSupplierEntities] = useState<Customer[]>([]);
	const [onlinePaymentsGateways, setOnlinePaymentsGateways] = useState<IntegrationType[]>([]);
	const [note, setNote] = useState('');
	const [cryptoWallet, setCryptoWallet] = useState<CryptoWallet>({ isCryptoWalletEnabled: false, cryptoWalletAddress: '', tokens: [] });
	const [settings, setSettings] = useState<PricingTermsSettings>();

	const userRole = useSelector((store: Store) => store.user.role);
	const dispatch = useDispatch();
	const { t } = useTranslation('translation');

	useEffect(() => {
		initSettings();
		setNote(pricingModelTab?.note || '');
	}, [
		pricingModelTab?.index,
		pricingModelTab?.supplierId,
		supplierEntities,
		pricingModelTab?.billingDetails?.paymentOptions,
		pricingModelTab?.netTerms,
	]);

	useEffect(() => {
		setSuppliers();
	}, []);

	useEffect(() => {
		setCryptoWallet({
			isCryptoWalletEnabled: pricingModelTab?.cryptoSettings?.isEnabled || false,
			cryptoWalletAddress: pricingModelTab?.cryptoSettings?.address,
			tokens: cryptoTokens.map((token) => ({
				id: token.id,
				isChecked: pricingModelTab?.cryptoSettings?.tokens?.includes(token.id) || false,
				label: token.title,
				iconSrc: getImageSrcByName(token.id),
			})),
		});
	}, [pricingModelTab?.index, pricingModelTab?.cryptoSettings?.isEnabled]);

	useEffect(() => {
		setPaymentGateways();
	}, [supplierEntities, pricingModelTab?.supplierId, pricingModelTab?.billingDetails?.paymentOptions]);

	useEffect(() => {
		settings?.isAutoChargeToggleOn && onUpdateAutoCharge();
	}, [settings?.isAutoChargeToggleOn]);

	const initSettings = () => {
		// Contract billing supports Wire Transfer only: Credit Card is hidden and the method is locked on,
		// so the terms always resolve to bank transfer regardless of any stored payment option.
		// preselectedBank is the supplier's default account, falling back to the first when none is marked default.
		const preselectedBank = updateBank();
		const paymentOptions = pricingModelTab?.billingDetails?.paymentOptions;
		const isWireOnly = paymentOptions?.length === 1 && paymentOptions[0] === PaymentOptions.BANK_TRANSFER;

		// Persist the wire-only option and pre-select the supplier's bank when either is missing.
		// instantUpdate=true so the draft's debounced/no-op save path doesn't drop the change. Guarded so
		// this converges (once wire-only + a bank are set, the effect that re-runs initSettings is a no-op).
		// Never persist in read-only mode (disabled) or on a non-draft contract (allowAutoPersist): viewing —
		// or editing a Canceled contract's — Billing settings must not silently mutate it.
		if (!disabled && allowAutoPersist && (!isWireOnly || (!pricingModelTab?.billingDetails?.billingAccountId && preselectedBank))) {
			updatePricingTermsData(
				{
					...pricingModelTab,
					billingDetails: {
						...pricingModelTab?.billingDetails,
						paymentOptions: [PaymentOptions.BANK_TRANSFER],
						billingAccountId: pricingModelTab?.billingDetails?.billingAccountId || preselectedBank?.id || null,
					},
				},
				true,
			);
		}

		setSettings(bankTransferSettings);
	};

	////STRIPE ACCOUNTS + CARDS SETTINGS
	const setPaymentGateways = () => {
		if (
			pricingModelTab?.billingDetails?.paymentOptions?.includes(PaymentOptions.PAYMENT_LINK) ||
			pricingModelTab?.billingDetails?.paymentOptions?.includes(PaymentOptions.AUTO_CHARGE)
		) {
			const linkedPaymentGateways = supplierEntities
				.find((company) => company.id === pricingModelTab?.supplierId)
				?.billingDetails?.paymentGateways?.filter((paymentGateway) => paymentGateway.connectionStatus == IntegrationStatus.LINKED);

			setOnlinePaymentsGateways(linkedPaymentGateways || []);
		}
	};

	const onChangeOnlinePayment = async (paymentGatewayId: string) => {
		updatePricingTermsData({
			...pricingModelTab,
			billingDetails: {
				...pricingModelTab?.billingDetails,
				paymentGatewayId,
				paymentGatewayCustomerId: null,
				paymentGatewayCustomer: null,
			},
		});
	};

	const onChangeStripeCard = async (paymentGatewayCustomerId: string, paymentGatewayCustomer: StripeCard | undefined) => {
		updatePricingTermsData(
			{
				...pricingModelTab,
				billingDetails: {
					...pricingModelTab?.billingDetails,
					paymentGatewayCustomer: paymentGatewayCustomer ?? null,
					paymentGatewayCustomerId: paymentGatewayCustomerId?.length ? paymentGatewayCustomerId : null,
				},
			},
			true,
		);
	};

	const onClickAutoCharge = async () => {
		updatePricingTermsData({
			...pricingModelTab,
			billingDetails: {
				...pricingModelTab?.billingDetails,
				paymentOptions: settings?.isAutoChargeToggleOn ? [PaymentOptions.PAYMENT_LINK] : [PaymentOptions.AUTO_CHARGE],
				billingAccountId: null,
			},
			cryptoSettings: {
				...pricingModelTab?.cryptoSettings,
				isEnabled: settings?.isAutoChargeToggleOn ? pricingModelTab?.cryptoSettings?.isEnabled : false,
			},
		});
	};

	const onUpdateAutoCharge = async () => {
		updatePricingTermsData({
			...pricingModelTab,
			billingDetails: {
				...pricingModelTab?.billingDetails,
				paymentOptions: [PaymentOptions.AUTO_CHARGE],
				billingAccountId: null,
			},
			cryptoSettings: {
				...pricingModelTab?.cryptoSettings,
				isEnabled: settings?.isAutoChargeToggleOn ? pricingModelTab?.cryptoSettings?.isEnabled : false,
			},
		});
	};

	////SUPPLIERS + BANKS SETTINGS
	const addEntity = async (supplier: Customer) => {
		setBanks(supplier?.billingDetails?.billingAccounts || []);
		const billingAccountId = supplier?.billingDetails?.billingAccounts?.find((bank: BillingAccountDetails) => bank.isDefault)?.id;
		await setSuppliers();
		pricingModelTab &&
			updatePricingTermsData(
				{
					...pricingModelTab,
					billingDetails: {
						...pricingModelTab.billingDetails,
						billingAccountId: settings?.isBankMethodSelected ? billingAccountId : null,
						paymentGatewayId: null,
					},
					supplierId: supplier?.id,
				},
				true,
			);
	};

	const onChangeEntity = async (supplierId: string) => {
		const billingAccountId = supplierEntities
			?.find((company) => company?.id === supplierId)
			?.billingDetails?.billingAccounts?.find((bank) => bank.isDefault)?.id;

		updatePricingTermsData(
			{
				...pricingModelTab,
				billingDetails: {
					...pricingModelTab?.billingDetails,
					billingAccountId: settings?.isBankMethodSelected ? billingAccountId : null,
					paymentGatewayId: null,
					paymentGatewayCustomerId: null,
					paymentGatewayCustomer: null,
				},
				supplierId,
			},
			true,
		);
	};

	const onChangeBank = async (billingAccountId?: string) => {
		updatePricingTermsData({
			...pricingModelTab,
			billingDetails: {
				...pricingModelTab?.billingDetails,
				billingAccountId,
			},
		});
	};

	const updateBank = () => {
		const banks = supplierEntities?.find((company) => company.id === pricingModelTab?.supplierId)?.billingDetails?.billingAccounts || [];
		setBanks(banks);
		return banks.find((bank) => bank.isDefault) ?? banks[0];
	};

	const setSuppliers = async () => {
		const companies = await getSuppliers(dispatch);
		setSupplierEntities(companies);
	};

	//CRYPTO SETTINGS
	const onUpdateCryptoTokens = (tags: CheckedTag[]) => {
		updatePricingTermsData({
			...pricingModelTab,
			cryptoSettings: {
				...pricingModelTab?.cryptoSettings,
				tokens: tags.filter((token) => token.isChecked).map((token) => token.id || '') as CryptoTokens[],
			},
		});
	};

	const updateCryptoWalletAddress = (address: string) => {
		updatePricingTermsData({
			...pricingModelTab,
			cryptoSettings: {
				...pricingModelTab?.cryptoSettings,
				address,
			},
		});
	};

	const updateCryptoWalletEnabled = (isCryptoWalletEnabled: boolean) => {
		setCryptoWallet({ ...cryptoWallet, isCryptoWalletEnabled });
		updatePricingTermsData(
			{
				...pricingModelTab,
				cryptoSettings: {
					...pricingModelTab?.cryptoSettings,
					isEnabled: isCryptoWalletEnabled,
				},
			},
			true,
		);
	};

	const onPaste = async (event: any) => {
		event.preventDefault();
		const paste = await navigator.clipboard.readText();
		setCryptoWallet({ ...cryptoWallet, cryptoWalletAddress: paste });
		updateCryptoWalletAddress(paste);
	};

	// Open entity modal in edit mode (per-item pencil).
	const onEditEntity = (entity: Customer) => {
		setEditEntity(entity);
		setIsCreateEntityOpened(true);
	};

	// On edit: refresh list/banks, keep selection. On create: addEntity selects the new entity.
	const onCreateUpdateEntity = async (entity: Customer) => {
		if (editEntity) {
			await setSuppliers();
			if (entity?.id === pricingModelTab?.supplierId) {
				setBanks(entity?.billingDetails?.billingAccounts || []);
			}
			return;
		}
		await addEntity(entity);
	};

	const onCloseEntityModal = () => {
		setIsCreateEntityOpened(false);
		setEditEntity(undefined);
	};

	const onUpdateNote = (val: string) => {
		setNote(val);
		updatePricingTermsData({
			...pricingModelTab,
			note: val,
		});
	};

	return (
		<div className={clsx(styles.container, className)} style={style}>
			{!hideTitle && <div className={styles.title}>{t('settings')}</div>}

			<div className={styles.content}>
				<div className={styles.rowItem}>
					<Select
						fullBorder
						disabled={disabled}
						placeholder={t('receivingEntityPlaceholder')}
						containerStyle={styles.fullWidthContainer}
						title={t('legalEntity')}
						onChange={onChangeEntity}
						defaultValue={pricingModelTab?.supplierId}
						width={''}
						data={
							supplierEntities?.map((item: Customer) => {
								const label = `${item?.name} ${item?.ein ? `(EIN ${item?.ein})` : ''}`;
								return {
									label,
									value: item.id,
									// Per-item edit pencil (Admins only); stopPropagation/closeMenu so it edits instead of selecting.
									customComponent: (_item, closeMenu) => (
										<div className={styles.entityOption}>
											<span className={styles.entityOptionLabel}>{label}</span>
											{userRole === UserRole.Admin && (
												<button
													type='button'
													className={styles.entityEditButton}
													aria-label={t('edit')}
													onClick={(event) => {
														event.stopPropagation();
														closeMenu?.();
														onEditEntity(item);
													}}
												>
													<Icon imgType='edit' width={1.4} color='neutral400' />
												</button>
											)}
										</div>
									),
								};
							}) || []
						}
						headerStyle={styles.selectHeaderStyle}
						addButton={{ title: t('addNewEntity'), onPress: () => setIsCreateEntityOpened(true), isDisabled: userRole !== UserRole.Admin }}
					/>
				</div>

				{pricingModelTab?.supplierId && (
					<>
						<div className={styles.rowItem}>
							<div className={styles.subTitle}>{t('paymentMethod')}</div>
							<div className={styles.flex}>
								{/* Contract billing supports Wire Transfer only: Credit Card is hidden and Wire is locked on. */}
								{constPaymentOptions
									.filter((item) => item.id === PaymentMethodOptions.BANK_TRANSFER)
									.map((item: { id: PaymentMethodOptions; title: string }) => (
										<Button
											key={item.id}
											type='outlined'
											color='neutral'
											className={clsx(styles.iconContainer, styles.selected, styles.locked)}
											onClick={() => undefined}
										>
											<Icon imgType={item.id as IconImgType} width={2.9} color='success900' />
											{item.title}
										</Button>
									))}
								{/* Crypto payment method is intentionally hidden in the V2 contract page. */}
							</div>
						</div>

						<Select
							fullBorder
							data={
								banks?.map((item: BillingAccountDetails) => ({
									label: getBankAccountString(item),
									value: item.id,
								})) || []
							}
							onChange={onChangeBank}
							defaultValue={pricingModelTab?.billingDetails?.billingAccountId || ''}
							width={''}
							optionsStyle={{ maxWidth: '15rem' }}
							className={styles.paymentMethodSelect}
							containerStyle={clsx(styles.selectContainer, !settings?.isBanksSelectDisplayed && styles.hidden)}
							title={t('bankTransfer')}
							// No bank accounts means the supplier's Legal Entity has no billing details yet — guide the user there.
							emptyState={{ title: t('noBankAccountsTitle'), subTitle: t('noBankAccountsSubtitle') }}
						/>

						{settings?.isStripeSelectDisplayed && (
							<div className={styles.selectsContainer}>
								<Select
									fullBorder
									data={
										onlinePaymentsGateways?.map((item) => {
											const isAirwallex = item?.platform === PaymentPlatform.AIRWALLEX;
											// Fallback to platform name so the row is never blank when metadata lacks accountName/accountId.
											const label =
												item?.metadata?.accountName ||
												item?.metadata?.accountId ||
												(isAirwallex ? 'Airwallex' : 'Stripe');
											return {
												label,
												value: item?.id,
												customComponent: () => (
													<div className={styles.stripeAccountItem}>
														{isAirwallex ? (
															<img alt='airwallex' src={images.airwallex} style={{ width: '2rem' }} />
														) : (
															<Icon imgType='stripe' color='calming' width={2} />
														)}
														{label}
													</div>
												),
											};
										}) || []
									}
									onChange={onChangeOnlinePayment}
									defaultValue={pricingModelTab?.billingDetails?.paymentGatewayId || undefined}
									width={''}
									className={styles.paymentMethodSelect}
									containerStyle={styles.selectContainer}
									title={t('onlinePayment')}
									placeholder={t('addOnlinePaymentAccount')}
									addButton={{
										title: t('connectOnlinePaymentAccount'),
										onPress: () => setIsProviderChooserOpen(true),
									}}
									inputIcon={
										onlinePaymentsGateways?.find((g) => g.id === pricingModelTab?.billingDetails?.paymentGatewayId)
											?.platform === PaymentPlatform.AIRWALLEX
											? undefined
											: { imgType: 'stripe', color: 'calming' }
									}
								/>
								<MenuList
									optionsList={[
										{
											title: t('connectAccountFor', { provider: 'Stripe' }),
											onPress: () => {
												setIsProviderChooserOpen(false);
												setNewPaymentAccountPlatform(PaymentPlatform.STRIPE);
											},
										},
										{
											title: t('connectAccountFor', { provider: 'Airwallex' }),
											onPress: () => {
												setIsProviderChooserOpen(false);
												setNewPaymentAccountPlatform(PaymentPlatform.AIRWALLEX);
											},
										},
									]}
									isMenuOpen={isProviderChooserOpen}
									openMenu={(open) => setIsProviderChooserOpen(open)}
									placement='bottom'
								>
									<span className={styles.providerChooserAnchor} />
								</MenuList>
								<Tooltip
									blackTooltip
									disabled={!settings.isAutoChargeToggleDisabled}
									tooltipComponent={
										<div className={styles.tooltipLabel}>
											<div>{t('whenUsingPayUponUsage')}</div>
											<div>{t('theAutoChargeRequired')}</div>
										</div>
									}
									className={styles.tooltip}
								>
									<div className={styles.autoCharge}>
										<span className={styles.autoChargeLabel}>{t('autoCharge')}</span>
										<Switch
											onChange={() => onClickAutoCharge()}
											defaultChecked={settings.isAutoChargeToggleOn}
											defaultDisable={settings.isAutoChargeToggleDisabled}
											className={clsx(styles.autoChargeToggle, settings.isAutoChargeToggleDisabled && styles.autoChargeToggleDisabled)}
										/>
									</div>
								</Tooltip>
							</div>
						)}

						{/* StripeCardSelect is Stripe-only; Airwallex auto-charge picks the last vaulted source server-side. */}
						{onlinePaymentsGateways?.find((g) => g.id === pricingModelTab?.billingDetails?.paymentGatewayId)?.platform !==
							PaymentPlatform.AIRWALLEX && (
							<StripeCardSelect
								onChange={(selectedId, selectedItem) => onChangeStripeCard(selectedId, selectedItem)}
								// needed to show which stripe account/card is selected
								defaultCustomer={pricingModelTab?.billingDetails?.paymentGatewayCustomer || undefined}
								paymentGatewayId={pricingModelTab?.billingDetails?.paymentGatewayId || undefined}
								supplierId={pricingModelTab?.supplierId}
								// suppliers needed in case supplier was changed
								iconType='information'
								showCheckoutComment
								suppliers={supplierEntities}
								className={styles.paymentMethodSelect}
								containerStyle={clsx(styles.selectContainer, settings?.isStripeCardsSelectDisplayed ? styles.appearAnimation : styles.hidden)}
								tooltipComponent={
									<div className={styles.tooltipLabel}>
										<div>{t('ifNoCardIsSelected')}</div>
										<div>{t('automaticPaymentLinkWillBe')}</div>
										<div>{t('sentToTheCustomer')}</div>
									</div>
								}
								displayCustomerNameOnly={!pricingModelTab?.billingDetails?.paymentOptions?.includes(PaymentOptions.AUTO_CHARGE)}
							/>
						)}

						<div className={clsx(styles.cryptoWalletInputContainer, cryptoWallet.isCryptoWalletEnabled ? styles.appearAnimation : styles.hidden)}>
							<Input
								value={cryptoWallet?.cryptoWalletAddress || ''}
								onChange={(val: string, event: ChangeEvent<HTMLInputElement>) => {
									setCryptoWallet({ ...cryptoWallet, cryptoWalletAddress: event.target.value });
									updateCryptoWalletAddress(event.target.value);
								}}
								title={t('receiptCryptoWallet')}
								placeholder={t('pasteHereYourWalletAddress')}
								containerClass={styles.fullWidthInput}
								rightChildren={
									<Tooltip tooltipComponent={t('paste')}>
										<Button color='neutral' type='link' className={styles.pasteButton} onClick={onPaste}>
											<Icon imgType='paste' width={2} color='neutral700'></Icon>
										</Button>
									</Tooltip>
								}
							/>
							<div className={styles.flex}>
								<CheckedTagsList data={cryptoWallet.tokens} onUpdateTags={onUpdateCryptoTokens} />
							</div>
						</div>
					</>
				)}

				<TextArea
					type='textarea'
					value={note}
					onChange={onUpdateNote}
					title={`${t('note')} (${t('optional')})`}
					className={styles.multiline}
					titleStyle={styles.noteTitle}
					placeholder={t('noteComment')}
				/>
			</div>

			<CreateSupplier
				companiesListLength={supplierEntities?.length || 0}
				isOpen={isCreateEntityOpened}
				title={t('legalEntityDetails')}
				defaultEntity={editEntity}
				closeModal={onCloseEntityModal}
				onCreateUpdateSupplier={onCreateUpdateEntity}
				selectedBankId={pricingModelTab?.billingDetails?.billingAccountId || null}
			/>

			<StripeConnectModal
				isOpen={newPaymentAccountPlatform === PaymentPlatform.STRIPE}
				platform={PaymentPlatform.STRIPE}
				provider={IntegrationProvider.STRIPE}
				image={images.stripePayment}
				onConnect={(id, integrationAccountId, existing) =>
					onConnectEntity(dispatch, IntegrationProvider.STRIPE, id, IntegrationPlatform.STRIPE, integrationAccountId, existing)
				}
				onCancel={(supplierId: string, integrationAccountId?: string) =>
					onCancelIntegration(dispatch, IntegrationProvider.STRIPE, supplierId, IntegrationPlatform.STRIPE, integrationAccountId)
				}
				onMissingDetails={(integrationAccountId: string) => onMissingDetailsEntity(dispatch, IntegrationProvider.STRIPE, integrationAccountId)}
				closeModal={() => setNewPaymentAccountPlatform(null)}
				onRefresh={setSuppliers}
			/>

			<StripeConnectModal
				isOpen={newPaymentAccountPlatform === PaymentPlatform.AIRWALLEX}
				platform={PaymentPlatform.AIRWALLEX}
				provider={IntegrationProvider.AIRWALLEX}
				image={images.airwallex}
				onConnect={(id, integrationAccountId, existing) =>
					onConnectEntity(dispatch, IntegrationProvider.AIRWALLEX, id, IntegrationPlatform.AIRWALLEX, integrationAccountId, existing)
				}
				onCancel={(supplierId: string, integrationAccountId?: string) =>
					onCancelIntegration(dispatch, IntegrationProvider.AIRWALLEX, supplierId, IntegrationPlatform.AIRWALLEX, integrationAccountId)
				}
				onMissingDetails={(integrationAccountId: string) => onMissingDetailsEntity(dispatch, IntegrationProvider.AIRWALLEX, integrationAccountId)}
				closeModal={() => setNewPaymentAccountPlatform(null)}
				onRefresh={setSuppliers}
			/>
		</div>
	);
};
